import { useEffect, useMemo, useRef, useState } from "react";
import { TextInput } from "react-native";
import { useAnimatedRef } from "react-native-reanimated";
import useContextMenu from "@/hooks/useContextMenu";
import { ThemedInputProps, ChoicesProp } from "./props";
import { choiceRenderer } from "./renderers";

/**
 * Custom hook for loading and managing choice items
 */
export function useChoiceItems<T>(choices?: ChoicesProp<T>) {
  const [items, setItems] = useState<T[]>();

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!choices) return;
      if (choices.kind === "array") {
        const src = choices.config.source;
        const data = typeof src === "function" ? await src() : src;
        if (!cancel) setItems(data);
      } else {
        const src = choices.config.source;
        const rec = typeof src === "function" ? await src() : src;
        const map = choices.config.map ?? (({ key }) => ({ key, label: String(key) }));
        const data = Object.entries(rec).map(([key, value]) => map({ key, value } as any));
        if (!cancel) setItems(data as T[]);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [choices]);

  return items;
}

/**
 * Custom hook for managing input validation state
 */
export function useInputValidation() {
  const [isValid, setIsValid] = useState(true);

  return { isValid, setIsValid };
}

/**
 * Custom hook for managing context menu functionality
 */
export function useInputContextMenu<T>(props: ThemedInputProps<T>) {
  const viewRef = useAnimatedRef();
  const inputRef = useRef<TextInput>(null);
  const contextMenu = useContextMenu();
  const items = useChoiceItems(props.choices);

  const menuNode = useMemo(() => {
    // prebuilt early return
    if (props.type === "date" || props.type === "date-time")
      return choiceRenderer({
        type: props.type,
        value: props.value, // Pass current value for date initialization
        onPick: (formattedValue) => {
          // The renderer now returns formatted display value using DateTimeProvider
          props.onChangeText?.(String(formattedValue));
          inputRef.current?.focus();
          // Don't close context menu for date types - let Apply button close it
        },
      });
    if (props.type !== "text")
      return choiceRenderer({
        type: props.type,
        onPick: () => {},
      });
    if (!props.items && !props.choices) return;
    if (!props.choices) {
      return choiceRenderer({
        type: props.type,
        items: props.items,
        onPick: (it) => {
          // Handle both string and object items
          const value = typeof it === "object" && it !== null && "text" in it ? it.text : String(it);
          props.onChangeText?.(value);
          contextMenu.setVisible(false);
        },
      });
    }
    if (props.choices.kind === "array") {
      const {
        getKey = (_: any, i: number) => String(i),
        getLabel = (x: any) => String(x),
        onPick,
      } = props.choices.config;
      return choiceRenderer({
        type: props.type,
        items,
        getKey,
        getLabel,
        onPick: (it) => {
          onPick?.(it);
          props.onChangeText?.(getLabel(it));
          contextMenu.setVisible(false);
        },
      });
    }

    const { onPick } = props.choices.config;
    return choiceRenderer({
      type: props.type,
      // Record/choices branch: items are always { key, label } here.
      items: (items ?? []) as { key: string; label: string }[],
      getKey: (it: { key: string; label: string }) => it.key,
      getLabel: (it: { key: string; label: string }) => it.label,
      onPick: (it: { key: string; label: string }) => {
        props.onChangeText?.(it.label);
        onPick?.({ key: it.key, value: undefined as any });
        contextMenu.setVisible(false);
      },
    });
  }, [props, items, contextMenu]);

  const showContextMenu = () => {
    if (menuNode) {
      inputRef.current?.measureInWindow((x, y, w, h) => {
        contextMenu.setIgnoreRef(inputRef);
        contextMenu.setTriggerElement?.({ x, y, width: w ?? 0, height: h ?? 0 });
        contextMenu.setLocation({ x, y: y + (h ?? 0) });
        contextMenu.setMenuContent(menuNode);
        contextMenu.setVisible(true);
      });
    }
  };

  return {
    viewRef,
    inputRef,
    contextMenu,
    menuNode,
    showContextMenu,
  };
}
